<template>
  <WdsStack direction-column gap-sm class="tw-rounded" :data-hire-id="hireId">
    <HirePageSectionHeader>
      <template #title>
        <WdsHeading class="tw-pl-1 tw-text-lg" type="title3">
          {{ $t('Contract summary') }}
        </WdsHeading>
      </template>
      <template #actions>
        <WdsButtonGhost
          v-if="!viewingAsSupplier"
          secondary
          class="tw-text-primary tw-font-medium"
          data-test="button-open-contract-drawer"
          @click="openContractDrawer('show')"
        >
          <WdsStack justify-center align-center gap-xs>
            {{ $t('View details') }}
            <WdsIcon name="chevron-right" :size="16" primary />
          </WdsStack>
        </WdsButtonGhost>
      </template>
    </HirePageSectionHeader>

    <Transition name="fade">
      <WdsStack v-if="loading" align-start direction-column gap-sm class="tw-w-full">
        <LoadingList :items="4" />
      </WdsStack>

      <WdsStack v-else-if="hire" direction-column gap-md>
        <!-- Worker/supplier layout: Timeframe, Rate, Engagement Type -->
        <WdsStack v-if="showWorkerLayout" gap-sm justify-space-between>
          <HireSummaryTimeframeCard :start-date="timeLine.start" :end-date="timeLine.end" />
          <HireSummaryRateCard
            v-if="contract"
            :currency="contract.currency ?? ''"
            :rate="contract.rate ?? 0"
            :rate-type="contract.rateType"
          />
          <WdsStack
            gap-sm
            direction-column
            justify-space-between
            class="tw-bg-true-gray-100 tw-rounded tw-grow tw-p-2"
          >
            <WdsStack align-center gap-xs justify-space-between>
              <WdsText lg class="tw-font-medium tw-text-gray-600">
                {{ $t('Engagement type') }}
              </WdsText>
              <template v-if="hire.usesClassification">
                <WdsTooltip
                  :text="
                    hire.classification?.overridesAnother
                      ? classificationOverridenTooltipText
                      : $t('Using Worksome Classify')
                  "
                  global
                >
                  <template v-if="hire.classification?.overridesAnother">
                    <DeterminationOverrideLogo size="16" />
                  </template>
                  <template v-else>
                    <DeterminationLogo truncated size="16" />
                  </template>
                </WdsTooltip>
              </template>
            </WdsStack>
            <WdsText lg>
              <HireClassification :row="hire" :stack-horizontally="true" />
            </WdsText>
          </WdsStack>
        </WdsStack>

        <!-- Default layout: Contract Status, Engagement Type, Timeframe -->
        <WdsStack v-else gap-sm justify-space-between>
          <WdsStack
            gap-sm
            direction-column
            justify-space-between
            class="tw-bg-true-gray-100 tw-rounded tw-grow tw-p-2"
          >
            <WdsStack gap-xs justify-space-between align-center>
              <WdsText lg class="tw-font-medium tw-text-gray-600">
                {{ $t('Contract status') }}
              </WdsText>
              <WdsTooltip
                v-if="contractStatus.edited"
                :text="
                  $t(
                    'The current contract is still active. A new version with updates has been sent to the worker for review and signature.',
                  )
                "
                global
              >
                <WdsTag sm>
                  <WdsStack align-center gap-xs>
                    {{ $t('Edited') }}
                    <WdsIcon :name="WdsIconNameEnum.HELP_FILLED" :size="12" />
                  </WdsStack>
                </WdsTag>
              </WdsTooltip>
            </WdsStack>
            <div>
              <WdsLabel
                :gray="contractStatus.gray"
                :success="contractStatus.success"
                :error="contractStatus.error"
                :warning="contractStatus.warning"
                :subtle="contractStatus.subtle"
                :label="contractStatus.text"
              />
            </div>
          </WdsStack>
          <WdsStack
            gap-sm
            direction-column
            justify-space-between
            class="tw-bg-true-gray-100 tw-rounded tw-grow tw-p-2"
          >
            <WdsStack align-center gap-xs justify-space-between>
              <WdsText lg class="tw-font-medium tw-text-gray-600">
                {{ $t('Engagement type') }}
              </WdsText>
              <template v-if="hire.usesClassification">
                <WdsTooltip
                  :text="
                    hire.classification?.overridesAnother
                      ? classificationOverridenTooltipText
                      : $t('Using Worksome Classify')
                  "
                  global
                >
                  <template v-if="hire.classification?.overridesAnother">
                    <DeterminationOverrideLogo size="16" />
                  </template>
                  <template v-else>
                    <DeterminationLogo truncated size="16" />
                  </template>
                </WdsTooltip>
              </template>
            </WdsStack>
            <WdsText lg>
              <HireClassification :row="hire" :stack-horizontally="true" />
            </WdsText>
          </WdsStack>
          <HireSummaryTimeframeCard :start-date="timeLine.start" :end-date="timeLine.end" />
        </WdsStack>

        <WdsStack gap-sm direction-column class="tw-px-2">
          <div class="tw-grid tw-grid-cols-3 tw-gap-4">
            <template v-if="showWorkerLayout && contract?.companyContact">
              <WdsText class="tw-font-medium">
                <span class="tw-text-gray-900">{{ $t('Contact person') }}</span>
              </WdsText>
              <WdsStack align-center gap-sm class="tw-col-span-2">
                <WdsAvatar
                  :path="getAvatarPath(contract.companyContact.avatar, contract.companyContact.id)"
                  :initials="
                    textHelpers.nameStringToInitials(contract.companyContact.name ?? '', 2)
                  "
                  xs
                />
                <WdsStack gap-xs class="tw-flex-wrap tw-min-w-0">
                  <WdsText>
                    {{ contract.companyContact.name }}
                  </WdsText>
                  <WdsText v-if="contract.companyContact.email" secondary class="tw-break-all">
                    <span class="tw-text-gray-900">{{ contract.companyContact.email }}</span>
                  </WdsText>
                </WdsStack>
              </WdsStack>
            </template>

            <template v-if="isCompany">
              <WdsText class="tw-font-medium">
                <span class="tw-text-gray-900">{{ $t('Rate') }}</span>
              </WdsText>
              <WdsText v-if="contract" class="tw-text-gray-900 tw-col-span-2">
                <span class="tw-font-normal">{{ formatContractRate(contract) }}</span>
              </WdsText>
            </template>

            <template v-if="isCompany && contract?.purchaseOrderNumber">
              <WdsText class="tw-font-medium">
                <span class="tw-text-gray-900">{{ $t('PO number') }}</span>
              </WdsText>
              <WdsText
                :secondary="!contract?.purchaseOrderNumber"
                class="tw-text-gray-900 tw-col-span-2"
              >
                <span class="tw-font-normal">
                  {{
                    contract?.purchaseOrderNumber
                      ? contract.purchaseOrderNumber
                      : $t('Not specified')
                  }}
                </span>
              </WdsText>
            </template>

            <WdsText>
              <span class="tw-text-gray-900">{{ $t('Primary work location') }}</span>
            </WdsText>
            <div class="tw-col-span-2">
              <WdsTag v-if="contract && contract.locationPreference">
                {{ locationPreferenceLabel(contract.locationPreference) }}
              </WdsTag>
            </div>

            <WdsText class="tw-font-medium">
              <span class="tw-text-gray-900">{{ $t('Work address') }}</span>
            </WdsText>
            <WdsText
              :secondary="!contract?.location?.formattedAddress"
              class="tw-text-gray-900 tw-col-span-2"
            >
              <span class="tw-font-normal">
                {{ contract?.location?.formattedAddress ?? $t('Not specified') }}
              </span>
            </WdsText>

            <WdsText class="tw-font-medium">
              <span class="tw-text-gray-900">{{ $t('Title') }}</span>
            </WdsText>
            <WdsText v-if="contract?.jobName" class="tw-text-gray-900 tw-col-span-2">
              <span class="tw-font-normal">{{ contract.jobName }}</span>
            </WdsText>

            <WdsText class="tw-font-medium">
              <span class="tw-text-gray-900">{{ $t('Description') }}</span>
            </WdsText>
            <div class="tw-col-span-2">
              <ClampedText v-if="contract?.jobDescription" :text="contract.jobDescription" />
            </div>
          </div>
        </WdsStack>

        <template v-if="isWorker && hire.usesClassification && hire.classification">
          <WdsDivider />
          <WdsStack align-center justify-space-between class="tw-px-2">
            <WdsStack direction-column gap-xs>
              <WdsStack justify-space-between align-center>
                <WdsStack align-center gap-xs>
                  <WdsIcon name="checkmark-outline" color="#047E43" :size="16" />
                  <WdsText class="tw-font-semibold">
                    <span>
                      {{ $t('Classification verified using Worksome Classify') }}
                    </span>
                  </WdsText>
                </WdsStack>
                <DeterminationLogo
                  :truncated="classificationTypeSlug !== 'ir35'"
                  :determination="classificationTypeSlug"
                  size="55"
                />
              </WdsStack>
              <WdsText small>
                {{
                  $t(
                    'Your engagement was classified to ensure your contract meets all local tax and legal standards.',
                  )
                }}
                <template v-if="marketIsUk">
                  {{ $t('You can dispute the result by') }}
                  <a
                    href="https://help.worksome.dk/en/articles/4957446-procedure-for-disputing-an-ir35-inside-determination"
                    target="_blank"
                    class="tw-underline tw-font-medium tw-text-gray-900"
                  >
                    {{ $t('following this link') }}.
                  </a>
                </template>
              </WdsText>
            </WdsStack>
          </WdsStack>
        </template>
      </WdsStack>
    </Transition>
  </WdsStack>
</template>

<script setup lang="ts">
import {
  WdsAvatar,
  WdsHeading,
  WdsStack,
  WdsIcon,
  WdsText,
  WdsButtonGhost,
  WdsLabel,
  WdsTag,
  WdsIconNameEnum,
  WdsDivider,
  WdsTooltip,
} from '@worksome/wds-components'
import { computed } from 'vue'
import { useQuery } from '@vue/apollo-composable'
import HirePageSectionHeader from '@/components/Hire/HirePageParts/HirePageSectionHeader.vue'
import { graphql, useFragment } from '@/gql'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'
import { useContractTimeLine } from '@/composables/useContractTimeLine'
import { MarketCode } from '@/gql/graphql'
import { textHelpers } from '@/utils/tools'
import {
  formatContractRate,
  getHireContractStatus,
  locationPreferenceLabel,
} from '@/utils/hireContract'
import { getAvatarPath } from '@/utils/avatar'
import { useHireContract } from '@/components/Hire/composables/useHireContract'
import { useContractDrawer } from '@/components/Hire/composables/useContractDrawer'
import DeterminationLogo from '@/components/compliances/Determinations/DeterminationLogo.vue'
import DeterminationOverrideLogo from '@/components/compliances/Determinations/DeterminationOverrideLogo.vue'
import ClampedText from '@/components/Common/ClampedText.vue'
import LoadingList from '@/components/Dashboard/Widgets/Utils/LoadingList.vue'
import HireClassification from '@/components/GraphqlDataTable/columns/Hire/HireEngagement.vue'
import HireSummaryTimeframeCard from '@/components/Hire/HireSummaryTimeframeCard.vue'
import HireSummaryRateCard from '@/components/Hire/HireSummaryRateCard.vue'
import { useAccountCapabilities } from '@/composables/useAccountCapabilities'
import { getClassificationTypeSlug } from '@/utils/classificationUtils'

import { useHireRecruiter } from '@/components/Hire/composables/useHireRecruiter'

const { isCompany, isWorker, isRecruiter, hasWorkerCapabilities } = useAccountCapabilities()

const props = defineProps<{
  hireId: string
}>()
const { viewerIsSupplier } = useHireRecruiter(props.hireId)
const viewingAsSupplier = computed(() => viewerIsSupplier.value || isRecruiter.value)
const showWorkerLayout = computed(() => viewingAsSupplier.value || isWorker.value)

const { openContractDrawer } = useContractDrawer()

const HireFragment = graphql(`
  fragment HireContractSummaryFields on Hire {
    id
    activeStatus
    usesClassification
    classification {
      id
      overridesAnother
      overrideReason
      overrideUser {
        name
      }
      pdfUrl
      type
    }
    ...HireCurrentContract
    pendingContractChanges
    worker {
      id
      name
    }
    conversation {
      id
    }
    ...HireEngagementColumn
  }
`)

const GET_HIRE = graphql(`
  query GetHireForContractSummary($id: ID!) {
    hire(id: $id) {
      ...HireContractSummaryFields
    }
  }
`)

const { result, loading } = useQuery(GET_HIRE, () => ({ id: props.hireId }), {
  clientId: 'publicApi',
})

const hire = computed(() => useFragment(HireFragment, result.value?.hire))
const contract = useHireContract(hire)

const timeLine = useContractTimeLine(contract)

const marketIsUk = computed(() => currentAccount?.market?.toUpperCase() === MarketCode.Uk)

const classificationTypeSlug = computed(() => {
  if (!hire.value?.classification?.type) return ''
  return getClassificationTypeSlug(hire.value.classification.type)
})

const classificationOverridenTooltipText = computed(() =>
  $t('The original classification was overridden by :user for the following reason: :reason', {
    user: hire.value?.classification?.overrideUser?.name,
    reason: hire.value?.classification?.overrideReason,
  }),
)

const contractStatus = computed(() =>
  getHireContractStatus(hire.value?.activeStatus, hire.value?.pendingContractChanges),
)
</script>
